import { SuperSearchPageResult, SuperSearchResult } from 'api';
import { shortDate } from 'utilities';
import { SUPER_SEARCH_MIN_REMOTE_SEARCH_LENGTH } from './SuperSearch.constants';
import type { SuperSearchTone } from './SuperSearch.styles';
import type { SuperSearchContext, SuperSearchDisplayItem, SuperSearchPendingPage } from './SuperSearch.types';

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const getBroaderSearchLabel = (context: SuperSearchContext, pendingPage?: SuperSearchPendingPage) => {
  if (pendingPage) {
    return undefined;
  }

  if (context.company && context.firm) {
    return `Search across ${context.firm.name}`;
  }

  return context.firm ? 'Search across all firms' : undefined;
};

export const getEmptyStateCopy = (normalizedQuery: string) => {
  if (normalizedQuery.length > 0 && normalizedQuery.length < SUPER_SEARCH_MIN_REMOTE_SEARCH_LENGTH) {
    return {
      description: `No local matches for “${normalizedQuery}” yet. Continue typing to search versions, saved views, valuation approaches, and documents.`,
      title: 'Continue typing to search more',
    };
  }

  if (normalizedQuery.length >= SUPER_SEARCH_MIN_REMOTE_SEARCH_LENGTH) {
    return {
      description: `No results matched “${normalizedQuery}”. Try a broader search or check the spelling.`,
      title: 'No exact matches',
    };
  }

  return {
    description: 'There are no destinations available in the current context.',
    title: 'No available destinations',
  };
};

export const getSearchPlaceholder = (context: SuperSearchContext, pendingPage?: SuperSearchPendingPage) => {
  if (pendingPage) {
    return `Choose a ${pendingPage.scope === SuperSearchPageResult.scope.FIRM ? 'firm' : 'company'} for ${pendingPage.label}`;
  }

  if (context.company) {
    return `Search ${context.company.name} documents, versions, views, approaches, pages, and dates...`;
  }

  if (context.firm) {
    return `Search ${context.firm.name} actions, settings, companies, documents, versions, views, and dates...`;
  }

  return 'Search firms, companies, actions, documents, versions, views, pages, and dates...';
};

export const getResultsAnnouncement = ({
  hasFatalError,
  hasPartialError,
  isEmptyQuery,
  isLoading,
  suggestedDestinationCount,
  resultCount,
}: {
  hasFatalError: boolean;
  hasPartialError: boolean;
  isEmptyQuery: boolean;
  isLoading: boolean;
  suggestedDestinationCount: number;
  resultCount: number;
}) => {
  if (isLoading) {
    return 'Searching Super Search.';
  }

  if (hasFatalError) {
    return 'Super Search could not load. Please try again.';
  }

  if (hasPartialError) {
    return `${resultCount} ${resultCount === 1 ? 'result is' : 'results are'} still available, but some Super Search results could not load. Retry to load the latest search.`;
  }

  if (suggestedDestinationCount > 0) {
    if (isEmptyQuery) {
      return `${suggestedDestinationCount} quick access ${suggestedDestinationCount === 1 ? 'destination is' : 'destinations are'} available.`;
    }

    return `No exact matches. ${suggestedDestinationCount} other ${suggestedDestinationCount === 1 ? 'destination is' : 'destinations are'} available.`;
  }

  return resultCount === 0
    ? 'No matching Super Search results.'
    : `${resultCount} ${resultCount === 1 ? 'result' : 'results'} available. Use the Up and Down Arrow keys to navigate.`;
};

export const getResultTone = (item: SuperSearchDisplayItem, sectionId: string): SuperSearchTone => {
  if (item.companyAction) {
    return 'companyAction';
  }

  if (sectionId === 'firm-actions' || sectionId === 'guided-tours') {
    return 'firmAction';
  }

  if (sectionId === 'firm-settings') {
    return 'firmSettings';
  }

  if (item.result.type === SuperSearchResult.type.MEASUREMENT_DATE) {
    return 'measurementDate';
  }

  if (item.result.type === SuperSearchResult.type.DOCUMENT) {
    return 'document';
  }

  if (
    item.result.type === SuperSearchResult.type.CAP_TABLE_VERSION ||
    item.result.type === SuperSearchResult.type.FINANCIAL_STATEMENT_VERSION ||
    item.result.type === SuperSearchResult.type.VALUATION_VERSION
  ) {
    return 'version';
  }

  if (item.result.type === SuperSearchResult.type.WATERFALL_VIEW) {
    return 'companyPage';
  }

  if (item.result.type === SuperSearchResult.type.VALUATION_APPROACH) {
    return 'companyAction';
  }

  if (item.result.type === SuperSearchResult.type.CUSTOM_VIEW) {
    return 'firmPage';
  }

  if (item.result.type === SuperSearchResult.type.FIRM) {
    return 'firm';
  }

  if (item.result.type === SuperSearchResult.type.COMPANY) {
    return 'company';
  }

  if (item.result.type === SuperSearchResult.type.FIRM_PAGE) {
    return 'firmPage';
  }

  return 'companyPage';
};

export const getSectionTone = (sectionId: string): SuperSearchTone => {
  switch (sectionId) {
    case 'firms':
      return 'firm';
    case 'companies':
      return 'company';
    case 'firm-actions':
    case 'guided-tours':
      return 'firmAction';
    case 'company-actions':
      return 'companyAction';
    case 'measurement-dates':
    case 'switch-measurement-date':
      return 'measurementDate';
    case 'documents':
      return 'document';
    case 'cap-table-versions':
    case 'financials-versions':
    case 'valuation-versions':
      return 'version';
    case 'waterfall-views':
      return 'companyPage';
    case 'valuation-approaches':
      return 'companyAction';
    case 'custom-views':
    case 'firm-pages':
      return 'firmPage';
    case 'firm-settings':
      return 'firmSettings';
    case 'company-pages':
      return 'companyPage';
    default:
      return 'neutral';
  }
};

export const getResultTypeLabel = (item: SuperSearchDisplayItem) => {
  if (item.typeLabel) {
    return item.typeLabel;
  }

  if (item.kind === 'guided-tour') {
    return 'Guided tour';
  }

  if (item.kind === 'command') {
    return 'Firm action';
  }

  if (item.kind === 'firm-setting') {
    return 'Firm setting';
  }

  if (item.kind === 'company-page-command') {
    return 'Company page';
  }

  if (item.kind === 'firm-page-command') {
    return 'Firm page';
  }

  if (item.kind === 'company-action') {
    return 'Company action';
  }

  if (item.result.type === SuperSearchResult.type.MEASUREMENT_DATE) {
    return 'Measurement date';
  }

  if (item.result.type === SuperSearchResult.type.DOCUMENT) {
    return 'Document';
  }

  if (item.result.type === SuperSearchResult.type.CAP_TABLE_VERSION) {
    return 'Cap Table version';
  }

  if (item.result.type === SuperSearchResult.type.FINANCIAL_STATEMENT_VERSION) {
    return 'Financials version';
  }

  if (item.result.type === SuperSearchResult.type.VALUATION_VERSION) {
    return 'Valuation version';
  }

  if (item.result.type === SuperSearchResult.type.VALUATION_APPROACH) {
    return 'Valuation approach';
  }

  if (item.result.type === SuperSearchResult.type.WATERFALL_VIEW) {
    return 'Waterfall view';
  }

  if (item.result.type === SuperSearchResult.type.CUSTOM_VIEW) {
    return item.result.custom_view?.page_type === 'valuations' ? 'Firm Valuations view' : 'Intelligence view';
  }

  return item.result.type.replace('_', ' ');
};

export const getNavigationContext = (item: SuperSearchDisplayItem) => {
  if (item.kind !== 'result' || item.result.type === SuperSearchResult.type.DOCUMENT) {
    return item.subtitle;
  }

  if (item.result.company) {
    return `${item.result.company.name} · ${item.result.firm.name}`;
  }

  return item.result.firm.name;
};

const getSubtitleSegmentTone = (
  item: SuperSearchDisplayItem,
  segment: string,
  segmentIndex: number
): SuperSearchTone => {
  if (item.companyAction && segment === item.page?.label) {
    return 'companyPage';
  }

  if (segment === item.result.company?.name) {
    return 'company';
  }

  if (item.result.measurement_date && /\d{2}\/\d{2}\/\d{4}/.test(segment)) {
    return 'measurementDate';
  }

  if (segment === item.result.firm.name) {
    return 'firm';
  }

  const isVersionResult =
    item.result.type === SuperSearchResult.type.CAP_TABLE_VERSION ||
    item.result.type === SuperSearchResult.type.FINANCIAL_STATEMENT_VERSION ||
    item.result.type === SuperSearchResult.type.VALUATION_VERSION;
  const isApproachVersion = item.result.type === SuperSearchResult.type.VALUATION_APPROACH && segmentIndex === 1;

  if ((isVersionResult && segmentIndex === 0) || isApproachVersion) {
    return 'version';
  }

  if (item.result.type === SuperSearchResult.type.VALUATION_APPROACH && segmentIndex === 0) {
    return 'companyAction';
  }

  return 'neutral';
};

const getSubtitleTonePriority = (item: SuperSearchDisplayItem, originalIndex: number, tone: SuperSearchTone) => {
  if (item.result.type === SuperSearchResult.type.VALUATION_APPROACH && originalIndex <= 1) {
    return originalIndex;
  }

  switch (tone) {
    case 'version':
      return 0;
    case 'companyPage':
      return 1;
    case 'companyAction':
    case 'neutral':
      return 2;
    case 'measurementDate':
      return 3;
    case 'company':
      return 4;
    case 'firm':
      return 5;
    default:
      return 6;
  }
};

export const getOrderedSubtitleSegments = (item: SuperSearchDisplayItem) =>
  item.subtitle
    .split(' / ')
    .map((segment, originalIndex) => {
      const tone = getSubtitleSegmentTone(item, segment, originalIndex);
      const formattedSegment =
        tone === 'version' && ISO_DATE_PATTERN.test(segment) ? (shortDate(segment) ?? segment) : segment;

      return {
        originalIndex,
        segment: formattedSegment,
        tone,
      };
    })
    .sort(
      (left, right) =>
        getSubtitleTonePriority(item, left.originalIndex, left.tone) -
        getSubtitleTonePriority(item, right.originalIndex, right.tone)
    );

export const canScopeItem = (item: SuperSearchDisplayItem, pendingPage?: SuperSearchPendingPage) =>
  item.kind === 'company-page-command' ||
  item.kind === 'firm-page-command' ||
  item.result.type === SuperSearchResult.type.FIRM ||
  (item.result.type === SuperSearchResult.type.COMPANY && Boolean(item.result.company)) ||
  (pendingPage?.scope === SuperSearchPageResult.scope.FIRM && item.result.type === SuperSearchResult.type.FIRM_PAGE) ||
  (pendingPage?.scope === SuperSearchPageResult.scope.COMPANY && Boolean(item.result.company));
